import { TenantStatus } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import { useGetTenant, useUpdateTenantStatus } from '@sunstone/api-client/hooks';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@sunstone/ui';
import { Building, Building2, ChevronDown, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { TenantDetail } from '@/features/tenants/TenantDetail';
import { TenantList } from '@/features/tenants/TenantList';
import { routes } from '@/lib/routes';
import { deferDialogOpen } from '@/lib/utils';

export function CustomersPage() {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { token } = useAuthToken();
  const { data: tenantData } = useGetTenant(tenantId ?? '', token);
  const tenant = tenantData?.tenant;
  const updateStatus = useUpdateTenantStatus(token);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { canManageAccess, hasPermission } = useAuthorization();

  if (tenantId) {
    const isPartner = tenant ? !tenant.parentId : false;
    const editPermission = isPartner ? 'edit:partner' : 'edit:tenant';
    const deletePermission = isPartner ? 'delete:partner' : 'delete:tenant';
    const isActive = tenant?.status === TenantStatus.ACTIVE;
    let statusLabel: ReactNode;
    if (updateStatus.isPending) {
      statusLabel = <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    } else {
      statusLabel = isActive ? 'Deactivate' : 'Activate';
    }

    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <PageHeader
          title={tenant?.displayName ?? '…'}
          description={
            isPartner ? (
              <>
                <Building2 className="h-3.5 w-3.5" />
                Partner
              </>
            ) : (
              <>
                <Building className="h-3.5 w-3.5" />
                Customer
              </>
            )
          }
          action={
            <div className="flex items-center gap-2">
              {(hasPermission(editPermission) ||
                hasPermission(deletePermission) ||
                canManageAccess) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={!tenant}>
                      More Actions
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canManageAccess && (
                      <>
                        <DropdownMenuItem onClick={() => navigate(routes.inviteMember(tenantId))}>
                          Invite Member
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(routes.newRole(tenantId))}>
                          New Role
                        </DropdownMenuItem>
                        {(hasPermission(editPermission) || hasPermission(deletePermission)) && (
                          <DropdownMenuSeparator />
                        )}
                      </>
                    )}
                    {hasPermission(editPermission) && (
                      <>
                        <DropdownMenuItem onClick={() => navigate(routes.editTenant(tenantId))}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          disabled={updateStatus.isPending}
                          onClick={() =>
                            tenant &&
                            updateStatus.mutate({
                              id: tenant.id,
                              status: isActive ? TenantStatus.INACTIVE : TenantStatus.ACTIVE,
                            })
                          }
                        >
                          {statusLabel}
                        </DropdownMenuItem>
                      </>
                    )}
                    {hasPermission(deletePermission) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => deferDialogOpen(() => setDeleteOpen(true))}
                        >
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {isPartner ? (
                <>
                  {hasPermission('create:deployment') && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!tenant}
                      onClick={() => navigate(`/tenants/${tenantId}/deployments/new`)}
                    >
                      New Deployment
                    </Button>
                  )}
                  {hasPermission('create:tenant') && (
                    <Button
                      size="sm"
                      disabled={!tenant}
                      onClick={() => navigate(routes.newCustomer(tenantId))}
                    >
                      New Customer
                    </Button>
                  )}
                </>
              ) : (
                hasPermission('create:deployment') && (
                  <Button
                    size="sm"
                    disabled={!tenant}
                    onClick={() => navigate(`/tenants/${tenantId}/deployments/new`)}
                  >
                    New Deployment
                  </Button>
                )
              )}
            </div>
          }
        />
        <TenantDetail
          tenantId={tenantId}
          deleteOpen={deleteOpen}
          onDeleteOpenChange={setDeleteOpen}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="Customers"
        description="Select a customer to view and manage its deployments and workspaces."
        action={
          hasPermission('create:tenant') && (
            <Button onClick={() => navigate(routes.newCustomer())}>New Customer</Button>
          )
        }
      />
      <TenantList mode="customers" urlParams />
    </div>
  );
}
